import { useQuery } from "@tanstack/react-query";
import {
  Building2, HardDriveDownload, History, KeyRound, Lock, LogOut, Network, ShieldAlert, ShieldCheck, Trash2, X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import {
  api, COR_ASSINATURA, dataBR, textoAssinatura, type AssinaturasResp, type SystemInfo,
} from "../lib/api";

interface Props {
  children: ReactNode;
  onNavigateHome: () => void;
  onAbrirLgpd?: () => void;
  onAbrirBackup?: () => void;
  onAbrirBackupCondominio?: () => void;
  usuario?: { nome: string; is_dono: boolean };
  /** Dono ou síndico: vê o selo da assinatura do(s) condomínio(s). */
  ehGestor?: boolean;
  /** Clique no selo da assinatura (abre o Uso & Custos). */
  onAbrirAssinaturas?: () => void;
  onSair?: () => void;
  onAbrirUsuarios?: () => void;
  onAbrirChaves?: () => void;
  onAbrirLixeira?: () => void;
  onAbrirAuditoria?: () => void;
}

interface BackupServidor {
  aplica: boolean;
  nivel: "ok" | "atencao" | "critico";
  problemas: string[];
  ultimo_ok?: string | null;
  copia_externa?: string | null;
}

/**
 * Layout base do app — header + main + footer CanadaTechBR.
 * 0.1.86: o selo da ASSINATURA (vencimento por condomínio) fica SEMPRE visível no
 * header para o Dono e o síndico — verde em dia, amarelo perto de vencer, vermelho
 * vencida. Substitui o antigo selo de licença por instalação.
 */
export function AppShell({ children, onNavigateHome, onAbrirLgpd, onAbrirBackup, onAbrirBackupCondominio, usuario, ehGestor, onAbrirAssinaturas, onSair, onAbrirUsuarios, onAbrirChaves, onAbrirLixeira, onAbrirAuditoria }: Props) {
  const { data: info } = useQuery<SystemInfo>({
    queryKey: ["sistema", "info"],
    queryFn: () => api.get<SystemInfo>("/info"),
  });
  // Assinatura do serviço (0.1.86): Dono vê todos os condomínios; síndico, os dele.
  const { data: assinaturas } = useQuery<AssinaturasResp>({
    queryKey: ["uso", "assinaturas"],
    queryFn: () => api.get<AssinaturasResp>("/uso/assinaturas"),
    enabled: !!usuario?.is_dono || !!ehGestor,
    refetchInterval: 30 * 60 * 1000,
  });
  // Backup automático do servidor (0.1.73): só o Dono vê (no DEV do notebook o
  // backend responde aplica=false).
  const { data: backupSrv } = useQuery<BackupServidor>({
    queryKey: ["sistema", "backup-servidor"],
    queryFn: () => api.get<BackupServidor>("/sistema/backup-servidor"),
    enabled: !!usuario?.is_dono,
    refetchInterval: 15 * 60 * 1000,
  });
  const [backupDispensado, setBackupDispensado] = useState(false);

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{
        background:
          "radial-gradient(900px 500px at 100% -5%, rgba(34,201,184,0.06), transparent 60%)," +
          "radial-gradient(760px 520px at 0% 100%, rgba(60,110,190,0.06), transparent 60%)," +
          "linear-gradient(180deg,#f7f9fc 0%,#eef2f8 100%)",
      }}
    >
      {info?.ambiente_rotulo && (
        // 0.1.81: faixa do ambiente de ENSAIO — impossível confundir com o oficial.
        <div role="status"
          className="sticky top-0 z-50 bg-amber-500 text-amber-950 text-center text-xs font-semibold py-1 px-3">
          {info.ambiente_rotulo === "HOMOLOGACAO" ? "HOMOLOGAÇÃO" : info.ambiente_rotulo} — ambiente de ensaio
          com cópia dos dados. Nada daqui vale para o condomínio real; não sai e-mail nem notificação.
        </div>
      )}
      <header className="bg-white/90 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={onNavigateHome}
            className="flex items-center gap-3 text-left hover:opacity-80 transition"
          >
            <div
              className="relative w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background:
                  "radial-gradient(130% 90% at 50% -20%, rgba(34,201,184,0.55), transparent 55%)," +
                  "linear-gradient(160deg,#17263f,#0f1a2e)",
                boxShadow: "0 6px 16px rgba(8,15,30,0.35), inset 0 1px 0 rgba(255,255,255,0.10)",
              }}
            >
              <span
                aria-hidden
                className="absolute inset-x-0 top-0 h-[2px] rounded-t-xl"
                style={{ background: "#22C9B8", boxShadow: "0 0 6px 1px rgba(34,201,184,0.9)" }}
              />
              <Building2 size={20} style={{ color: "#22C9B8" }} />
            </div>
            <div>
              <h1 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                Síndico<span className="text-teal-600">App</span>
                <span className="text-[11px] font-semibold text-brand-700 bg-brand-50 border border-brand-100 rounded px-1.5 py-0.5">
                  v{info?.version ?? "—"}
                </span>
                {info?.env && info.env !== "prod" && (
                  <span className="text-[10px] font-medium text-amber-800 bg-amber-100 rounded px-1.5 py-0.5">
                    {info.env.toUpperCase()}
                  </span>
                )}
              </h1>
              <p className="text-xs text-slate-500">
                Auditoria de balancetes
                {info?.iniciado_em && (
                  <span
                    className="ml-2 text-[10px] text-slate-400"
                    title="Quando o servidor (backend) subiu esta versão do código. Se você reiniciou o app e este horário NÃO mudou, o backend não recarregou o código novo."
                  >
                    · atualizado {new Date(info.iniciado_em).toLocaleString("pt-BR", {
                      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                )}
              </p>
            </div>
          </button>

          <div className="flex items-center gap-2">
            {onAbrirBackup && (
              <button
                type="button"
                onClick={onAbrirBackup}
                title="Backup e restauração dos seus dados"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border text-emerald-700 bg-emerald-50 border-emerald-200 hover:brightness-95 transition"
              >
                <HardDriveDownload size={16} />
                <span className="hidden sm:inline">Backup</span>
              </button>
            )}
            {onAbrirBackupCondominio && (
              <button
                type="button"
                onClick={onAbrirBackupCondominio}
                title="Backup e restauração dos condomínios que você gerencia"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border text-emerald-700 bg-emerald-50 border-emerald-200 hover:brightness-95 transition"
              >
                <HardDriveDownload size={16} />
                <span className="hidden sm:inline">Backup</span>
              </button>
            )}
            {onAbrirLgpd && (
              <button
                type="button"
                onClick={onAbrirLgpd}
                title="Privacidade & Dados (LGPD): senha de acesso e proteção dos dados"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border text-slate-700 bg-slate-100 border-slate-200 hover:brightness-95 transition"
              >
                <Lock size={16} />
                <span className="hidden sm:inline">Privacidade</span>
              </button>
            )}
            <AssinaturaBadge dados={assinaturas} onClick={onAbrirAssinaturas} />
            {usuario && onAbrirUsuarios && usuario.is_dono && (
              <button
                type="button"
                onClick={onAbrirUsuarios}
                title="Usuários e permissões (só o dono)"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border text-brand-700 bg-brand-50 border-brand-200 hover:brightness-95 transition"
              >
                <KeyRound size={16} />
                <span className="hidden sm:inline">Usuários</span>
              </button>
            )}
            {usuario && onAbrirChaves && usuario.is_dono && (
              <button
                type="button"
                onClick={onAbrirChaves}
                title="Chaves de API por condomínio (só o dono)"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border text-brand-700 bg-brand-50 border-brand-200 hover:brightness-95 transition"
              >
                <Network size={16} />
                <span className="hidden sm:inline">Chaves</span>
              </button>
            )}
            {usuario && onAbrirLixeira && usuario.is_dono && (
              <button
                type="button"
                onClick={onAbrirLixeira}
                title="Lixeira: condomínios apagados (restaurar em até 30 dias) — só o dono"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border text-amber-700 bg-amber-50 border-amber-200 hover:brightness-95 transition"
              >
                <Trash2 size={16} />
                <span className="hidden sm:inline">Lixeira</span>
              </button>
            )}
            {usuario && onAbrirAuditoria && usuario.is_dono && (
              <button
                type="button"
                onClick={onAbrirAuditoria}
                title="Trilha de auditoria: quem alterou o quê e quando (só o dono)"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border text-slate-700 bg-slate-100 border-slate-200 hover:brightness-95 transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">Auditoria</span>
              </button>
            )}
            {usuario && (
              <div className="flex items-center gap-2 pl-2 ml-1 border-l border-slate-200">
                <span className="text-xs text-slate-600 hidden sm:inline" title={usuario.is_dono ? "Administrador (dono)" : undefined}>
                  {usuario.nome.split(" ")[0]}
                </span>
                <button
                  type="button"
                  onClick={onSair}
                  title="Sair"
                  className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50 transition"
                >
                  <LogOut size={16} />
                  <span className="hidden sm:inline">Sair</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {backupSrv?.aplica && backupSrv.nivel !== "ok" && !backupDispensado && (
        <div className={`${backupSrv.nivel === "critico" ? "bg-red-600" : "bg-amber-500"} text-white text-sm`}>
          <div className="max-w-7xl mx-auto px-6 py-2 flex items-start gap-2">
            <ShieldAlert size={16} className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <b>{backupSrv.nivel === "critico" ? "Backup do servidor com problema" : "Atenção ao backup"}</b>
              {" — "}
              {backupSrv.problemas.join(" ")}
              <span className="opacity-90">
                {" "}(último backup bom: {backupSrv.ultimo_ok ?? "nenhum"}; cópia no notebook: {backupSrv.copia_externa ?? "nenhuma"})
              </span>
            </div>
            <button onClick={() => setBackupDispensado(true)} className="shrink-0 opacity-80 hover:opacity-100"
              title="Esconder até recarregar a página (o aviso volta enquanto o problema continuar)">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">{children}</main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between text-xs text-slate-500 flex-wrap gap-2">
          <span>
            SíndicoApp v{info?.version ?? "0.1.0"}
            {info?.env && info.env !== "prod" && (
              <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
                {info.env.toUpperCase()}
              </span>
            )}
          </span>
          <span>Direitos de Licença reservados à CanadaTechBR.</span>
        </div>
      </footer>

    </div>
  );
}

// =====================================================================
// Selo da assinatura (0.1.86) — sempre visível para Dono e síndico
// =====================================================================
function AssinaturaBadge({ dados, onClick }: { dados?: AssinaturasResp; onClick?: () => void }) {
  if (!dados || dados.resumo.total === 0) return null;
  const r = dados.resumo;
  let texto: string;
  if (r.total === 1) {
    const i = dados.itens[0];
    texto = i.nivel === "sem_data" ? "Assinatura: sem data" : `Assinatura: ${textoAssinatura(i)}`;
  } else if (r.nivel === "vencida") {
    texto = `Assinaturas: ${r.vencidas} vencida${r.vencidas === 1 ? "" : "s"}`
      + (r.a_vencer ? ` · ${r.a_vencer} a vencer` : "");
  } else if (r.nivel === "a_vencer") {
    texto = `Assinaturas: ${r.a_vencer} a vencer · próx. ${dataBR(r.proximo_vencimento)}`;
  } else if (r.nivel === "sem_data") {
    texto = `Assinaturas: ${r.sem_data} sem data`;
  } else {
    texto = `Assinaturas em dia · próx. ${dataBR(r.proximo_vencimento)}`;
  }
  const dica = dados.itens
    .map((i) => `${i.nome}: ${textoAssinatura(i)}`)
    .join("\n");
  const Icone = r.nivel === "em_dia" ? ShieldCheck : ShieldAlert;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${dica}\n\nClique para ver em Uso & Custos.`}
      className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border hover:brightness-95 transition ${COR_ASSINATURA[r.nivel]}`}
    >
      <Icone size={16} />
      <span className="hidden md:inline">{texto}</span>
      <span className="md:hidden">
        {r.nivel === "vencida" ? "Vencida" : r.nivel === "a_vencer" ? "A vencer" : r.nivel === "sem_data" ? "Sem data" : "Em dia"}
      </span>
    </button>
  );
}
